import React, { useEffect, useMemo, useRef } from 'react';

import {
  FilterFixed,
  useFilterDataHook,
} from '@/pages/opinion/components/FilterCommon';
import KeywordBox from '@/pages/opinion/pages/feeds/components/KeywordBox';

import { connect, useIntl } from 'umi';
import FilterBoxCommon from '@/pages/opinion/components/FilterBoxCommon';
import CardCommon from '@/pages/opinion/components/CardCommon';
import CardLoading from '@/pages/opinion/components/CardLoading';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import useSentimentSelect from '../../hooks/useSentimentSelect';
import { useInitBuriedPointInfo } from '@/pages/opinion/operationLog/buriedPoint';
import { getSearchText } from '../../constants';
import { getRealParams } from '@/pages/opinion/utils/params';
import { useSocialMediaColumns } from '@/pages/opinion/components/TableCommonHook/hooks/useSocialMediaColumns';
import TableCommonHook from '@/pages/opinion/components/TableCommonHook';
import useFeedsData from '../../hooks/useFeedsData';
import {
  useRequestComments,
  useRequestKeyword,
  useRequestLatestEditLog,
  useRequestStats,
} from '../../hooks/useFeedsRequest';
import '@/pages/opinion/assets/scss/pages/feeds/SocialMedia.scss';
import { useGetSingePageSourceRule } from '@op/hooks/useGetPageSourceRule';
import { useWindowSize } from '@op/hooks/useResize';
import { useFeedsGpt } from '../../hooks/useFeedsGpt';
import {
  beforeCacheDealwithData,
  getSortOptions,
} from '../../constants/filter';
import { dealwithOtherFilter } from '../../utils';
import { buildSearchParams } from '../../utils/searchMode';
import { channelSplitFlag } from '@op/components/FilterCommon/constants/channel';
import useLanguage from '@op/hooks/useLanguage';
const defaultTableList: any = [];
function SocialMedia({
  userInfo,
  gameInfo,
  wholeGameInfo,
  tableSticky = true,
  scrollBody,
  pageName = 'Feeds_Social_Media',
  tabText = 'Social Media',
  specialParams = {},
  isSpecialChannel = false,
  showAigcBtn = false,
  buriedPointInfo,
  filterDefaultValue = {},
  filterDefaultConfig = {},
}: any) {
  // console.log(feedsKeyword, feedsComments, feedsStats, '-*--------*');
  const { formatMessage } = useLanguage();
  const { Sentiment_Rating } = useGetSingePageSourceRule({
    keys: 'Sentiment_Rating',
  });
  const { download } = useDownloadParams({
    maxRows: 600 * 1000,
    exceedMessage: formatMessage(
      'Downloads exceed the {num} limit. Please adjust filters and retry.',
      {num: "600K"},
    ),
  });
  const { feedsStats, feedsStatsLoading, requestState } = useRequestStats({});
  const { feedsKeyword, feedsKeywordLoading, requestKeyword } =
    useRequestKeyword();
  const {
    feedsComments,
    feedsCommentsLoading,
    requestComments,
    requestSearchComments,
    feedsSearchComments,
    feedsSearchCommentsLoading,
  } = useRequestComments({});

  useInitBuriedPointInfo({ buriedPoint: buriedPointInfo });

  const {
    pageInfo,
    sortBy,
    matchModel,
    search,
    selectKeyword,
    defaultKeyword,
    selectRenderKeyword,
    isExcludeInvalid,
    hasSearch,
    isExactOrExtendSearch,
    showSentiment,
    changePageInfo,
    setSortBy,
    setIsExcludeInvalid,
    resetPartFeedsData,
    filterBoxChange,
    filterBoxSearch,
    filterBoxClear,
    keywordChange,
    languages,
    setLanguages,
    channelList,
    setChannelList,
  } = useFeedsData({
    storageKey: `feedsSocialMedia_${gameInfo.edition_unified_id}`,
  });

  const { comments, loading, tableList } = useMemo(() => {
    const comments = isExactOrExtendSearch
      ? feedsSearchComments
      : feedsComments;
    const loading = isExactOrExtendSearch
      ? feedsSearchCommentsLoading
      : feedsCommentsLoading;
    return {
      comments,
      loading,
      tableList: comments?.list || defaultTableList,
    };
  }, [
    isExactOrExtendSearch,
    feedsSearchComments,
    feedsComments,
    feedsSearchCommentsLoading,
    feedsCommentsLoading,
  ]);

  const requestFunction = isExactOrExtendSearch
    ? requestSearchComments
    : requestComments;

  const {
    loading: initLoading,
    filter,
    changeFilter,
    changeOtherPageParams,
    filterOptions,
    isFirst,
    filterCommonParams,
  } = useFilterDataHook({
    gameInfo,
    hasHero: true,
    pageName,
  });

  const { sentimentSelect, setSentimentSelect } = useSentimentSelect({
    filter,
    changeFilter,
  });
  // const { hasSentimentSentences, setHasSentimentSentences } =
  //   useSentimentSentences({});
  const commonParams = useMemo(() => {
    // 模糊搜索使用 search_content 字段，精准/拓展搜索使用 search_data 字段
    const searchParams = buildSearchParams(matchModel, search, hasSearch);
    return {
      purpose: [],
      sort_item: sortBy.by,
      sort_model: sortBy.order,
      // 模糊搜索不隐藏 keyword，精准/拓展搜索隐藏 keyword
      keyword: isExactOrExtendSearch ? [] : selectKeyword,
      ...pageInfo,
      ...searchParams,
    };
    // isExactOrExtendSearch 已包含 matchModel 变化逻辑（exact/extend 切换不改变 search_data 字段）
    // hasSearch 变化时，闭包中的 matchModel 已是当前最新值
  }, [
    pageInfo,
    sortBy,
    search,
    selectKeyword,
    isExactOrExtendSearch,
    hasSearch,
  ]);

  const fetchComments = () => {
    if (!userInfo || !gameInfo || !filter) return;

    const realParams = getRealParams({
      filter,
      commonParams,
      isExcludeInvalid,
    });

    requestFunction({
      ...realParams,
      ...gameInfo,
      ...specialParams,
      user_id: userInfo.userid,
      translated_to: 'origin',
      need_topic_translation: true,
      // `/feeds/comments` 接口不接受 match_model，仅 `/feeds/search` 需要
      ...(isExactOrExtendSearch ? { match_model: matchModel } : {}),
    });
  };

  useEffect(() => {
    fetchComments();
  }, [userInfo, filter, commonParams, isExcludeInvalid]);

  // 仅处理 commonParams 不变（exact↔extend）的 matchModel 切换，避免重复请求
  const prevIsExactOrExtendSearchRef = useRef(isExactOrExtendSearch);
  useEffect(() => {
    const prev = prevIsExactOrExtendSearchRef.current;
    prevIsExactOrExtendSearchRef.current = isExactOrExtendSearch;

    // 无搜索内容或 isExactOrExtendSearch 已变（由主 effect 通过 commonParams 处理），跳过
    if (!hasSearch || prev !== isExactOrExtendSearch) return;

    fetchComments();
  }, [matchModel]);

  useEffect(() => {
    if (!userInfo || !gameInfo || !filter) return;

    requestState({
      ...filter,
      ...gameInfo,
      ...specialParams,
      user_id: userInfo.userid,
      translated_to: 'origin',
    });

    requestKeyword({
      ...filter,
      ...gameInfo,
      ...specialParams,
      user_id: userInfo.userid,
      keyword_sample_size: '500',
      translated_to: 'origin',
    });
  }, [userInfo, filter]);

  const { GptBtnElement, GptElement } = useFeedsGpt({
    filter,
    commonParams,
    gameInfo,
    specialParams,
    // `/feeds/comments` 接口不接受 match_model，仅 `/feeds/search` 需要
    ...(isExactOrExtendSearch ? { match_model: matchModel } : {}),
    wholeGameInfo,
    gptName: tabText,
    feedsStats
  });

  const { logCommentMap, loading: logLoading } = useRequestLatestEditLog({
    data: tableList as any,
  });

  const { columns, sortColumns, selectSortKeys } = useSocialMediaColumns({
    isSpecialChannel,
    gameInfo,
    wholeGameInfo,
    languages,
    renderKey: hasSearch ? search : selectRenderKeyword,
    total: hasSearch ? comments?.total : undefined,
    sort: sortBy,
    setSort: setSortBy,
    isExcludeInvalid,
    setIsExcludeInvalid,
    showSentiment,
    logCommentMap,
    logLoading,
    setLanguages,
    channelList,
  });

  const sortOptions = useMemo(() => {
    const sortKeys = [...selectSortKeys];

    if (hasSearch) {
      sortKeys.unshift('relevance');
    }

    const orderOptions = getSortOptions(sortKeys);

    return orderOptions;
  }, [selectSortKeys]);

  const { windowSize } = useWindowSize();

  return (
    <div className="feeds-socialmedia">
      <FilterFixed
        filter={filterOptions}
        confirm={(value: any) => {
          if (!isFirst) {
            setSentimentSelect(undefined);
            resetPartFeedsData();
          }
          changeFilter(value);
        }}
        defaultConfig={{
          channel: {
            channelKey: 'social',
            firstExcludeOptions: [`social${channelSplitFlag}hok_cbt_chatting`],
          },
          ...filterDefaultConfig,
        }}
        defaultValue={filterDefaultValue}
        otherRequestType={['sentiment_sentences_config']}
        {...filterCommonParams}
        finallyDealwithDefaultValue={(
          value,
          otherPageParams,
          isDefaultValue = false,
        ) => {
          if (!isDefaultValue) {
            changeOtherPageParams(dealwithOtherFilter(otherPageParams));
          }
          return value;
        }}
        backCondOriginData={(data) => {
          const { channel } = data;
          setChannelList(channel?.social || []);
        }}
        beforeCacheDealwithData={beforeCacheDealwithData}
        scrollBody={scrollBody}
      ></FilterFixed>
      <CardLoading loading={initLoading}>
        {/* 模糊搜索不隐藏 keyword，精准/拓展搜索隐藏 keyword */}
        {!isExactOrExtendSearch && (
          <CardCommon
            loading={feedsKeywordLoading || feedsStatsLoading}
            loadingHeight={200}
            className="social-media-keyword-box"
          >
            <KeywordBox
              keywords={feedsKeyword}
              defaultSelectedKeyword={defaultKeyword}
              tableTotal={comments?.total}
              commentLoading={feedsCommentsLoading}
              keywordChange={(keyword) => {
                keywordChange(keyword);
              }}
              setSentimentSelect={(value: any) => {
                changePageInfo();
                setSentimentSelect(value);
              }}
              sentimentSelect={sentimentSelect}
              GptBtnElement={GptBtnElement}
              data={feedsStats as any}
            />
          </CardCommon>
        )}

        {GptElement}

        <FilterBoxCommon
          left={[
            {
              key: 'gpt',
              show: showAigcBtn && hasSearch,
              element: GptBtnElement,
            },
            {
              key: 'sortBy',
              valueKey: 'sortBy-1',
              defaultValue: sortBy.by,
              defaultParams: {
                options: sortOptions,
              },
              show: !hasSearch,
            },
            {
              key: 'sortBy',
              valueKey: 'sortBy-2',
              defaultValue: sortBy.by,
              defaultParams: {
                options: sortOptions,
              },
              show: hasSearch,
            },
            // {
            //   key: 'singleCheckbox',
            //   valueKey: 'showSentiment',
            //   defaultValue: showSentiment,
            //   defaultParams: {
            //     text: 'Sub Sentiment',
            //     tipsMap: commonSentimentSentencesTips,
            //   },
            //   show: !Sentiment_Rating,
            // },
          ]}
          right={[
            [
              {
                key: 'match_model',
                defaultValue: matchModel,
              },
              {
                key: 'search',
                defaultValue: search,
                defaultParams: {
                  className: 'dbo-search-input',
                  placeholder: formatMessage({
                    id: getSearchText({
                      isSearch: hasSearch,
                      width: windowSize.width,
                    }),
                  }),
                  onSearch: filterBoxSearch,
                  onClear: filterBoxClear,
                },
              },
            ],
            {
              key: 'download',
              event: {
                onClick: () => {
                  if (!comments?.total) return;

                  const fileName = `${tabText} Feeds`;

                  const downloadFilter = {
                    ...filter,
                    ...gameInfo,
                    ...commonParams,
                    ...specialParams,
                    page: 1,
                    page_size: 1000,
                    need_parent_comment_url: true,
                    // `/feeds/comments` 接口不接受 match_model，仅 `/feeds/search` 需要
                    ...(isExactOrExtendSearch
                      ? { match_model: matchModel }
                      : {}),
                  };

                  // 精准/拓展搜索走前端分页下载（xlsx）
                  // 模糊搜索/无搜索：总量 <= 20K 走前端分页下载（xlsx，使用 comments 接口），否则走后端直接下载（csv）
                  const total = comments?.total || 0;
                  const needPageDownload =
                    isExactOrExtendSearch || total <= 20000;
                  const downloadKey = needPageDownload
                    ? 'FeedsSocialMediaSearchDownload'
                    : 'FeedsSocialMediaDownload';

                  download(
                    {
                      key: downloadKey,
                      fileName,
                      filter: downloadFilter,
                      auxiliaryParams: {
                        metrics: [], // 空数组表示导出所有列
                        hideSentimentRating: Sentiment_Rating,
                        channelList,
                        isExactOrExtendSearch,
                      },
                    },
                    total,
                  );
                },
              },
            },
          ]}
          onChange={filterBoxChange}
          minModal={!Sentiment_Rating}
          className={hasSearch ? 'dbo-search-info' : ''}
        ></FilterBoxCommon>

        <CardCommon
          loading={loading}
          loadingHeight={300}
        // empty={isEmpty}
        // emptyHeight={300}
        >
          <TableCommonHook
            minWidth={1600}
            recordKey={'comment_uin'}
            tableSticky={tableSticky}
            records={comments?.list || []}
            total={comments?.total || 0}
            pageInfo={pageInfo}
            setPageInfo={changePageInfo}
            columns={columns}
            sortColumns={sortColumns}
            sortInfo={[sortBy, setSortBy]}
          />
        </CardCommon>
      </CardLoading>
    </div>
  );
}

export default connect((state: any) => {
  const { loading, home, opinionCommon, opinionFeedsFilter } = state;

  return {
    userInfoLoading: loading.effects['home/getUserInfo'],
    userInfo: home.userInfo,
    gameInfo: opinionCommon.gameInfo,
    wholeGameInfo: opinionCommon.wholeGameInfo,
    filter: opinionFeedsFilter,
  };
})(SocialMedia);
